import { useTranslation } from "react-i18next";
import { CheckCircle, Clock, XCircle } from "lucide-react";

import { StatusBadge } from "@/components/shared/StatusBadge";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { cn } from "@/lib/utils";

/** Mirrors `InvoiceDetailHeader` status strip (accepted / pending / rejected). */
const ACCENT_BORDER_CLASS = {
  accepted: "border-l-green-500",
  pending: "border-l-blue-500",
  rejected: "border-l-red-400",
} as const;

const VARIANT_ICON_CLASS: Record<StatusBadgeVariant, string> = {
  success: "text-green-600 dark:text-green-500",
  warning: "text-yellow-600 dark:text-yellow-500",
  danger: "text-red-600 dark:text-red-500",
  info: "text-blue-600 dark:text-blue-500",
  neutral: "text-slate-600 dark:text-slate-400",
  softDanger: "text-red-600 dark:text-red-500",
};

type BannerKind = "accepted" | "rejected" | "pending";

function recibidaEstadoKind(estado: string | undefined): BannerKind {
  if (estado === "AE") return "accepted";
  if (estado === "BE") return "rejected";
  return "pending";
}

function badgeVariant(kind: BannerKind): StatusBadgeVariant {
  if (kind === "accepted") return "success";
  if (kind === "rejected") return "danger";
  return "info";
}

export interface RecibidaDetailStatusBannerProps {
  estado: string | undefined;
}

/**
 * Same visual pattern as emitted-invoice {@link InvoiceDetailHeader} (border + badge + icon).
 * Copy uses `recibidas.statusAE` / `recibidas.statusBE` so the detail view matches the list table.
 */
export function RecibidaDetailStatusBanner({
  estado,
}: RecibidaDetailStatusBannerProps): JSX.Element {
  const { t } = useTranslation();
  const kind = recibidaEstadoKind(estado);
  const variant = badgeVariant(kind);

  const StatusIcon = kind === "accepted" ? CheckCircle : kind === "rejected" ? XCircle : Clock;

  const title =
    kind === "accepted"
      ? t("recibidas.statusAE", "Document received")
      : kind === "rejected"
        ? t("recibidas.statusBE", "Rejected")
        : t("recibidas.detailBanner.unrecognized", "Reported status: {{code}}", {
            code: (estado ?? "").trim() !== "" ? estado! : "—",
          });

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card p-6",
        "border-l-2",
        ACCENT_BORDER_CLASS[kind]
      )}
      role="status"
      aria-label={title}
    >
      <div className="flex items-center gap-2">
        <StatusBadge variant={variant}>
          <StatusIcon
            className={cn("mr-2 h-4 w-4 shrink-0", VARIANT_ICON_CLASS[variant])}
            aria-hidden
          />
          {title}
        </StatusBadge>
      </div>
    </div>
  );
}
